import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { customerAccountFacts } from '../../../../shared/customer-account.mjs';
import { accountLabel, awsAccountDisplayName, marketplaceAccountDisplayName } from '../../../../shared/account-names.mjs';
import AccountNamesField from '../../components/AccountNamesField.jsx';
import { MissingPermissionsNote } from '../../components/LifecycleNotice.jsx';
import TrustStackNotice from '../../components/TrustStackNotice.jsx';
import Notice from '../../components/Notice.jsx';
import { pushResultNotice, resourcesStatus } from '../../lib/deployment-health.js';
import { TRUST_STACK_VERSION, trustStackOutdated } from '../../../../shared/trust-stack.mjs';
import StatusBadge from '../../components/StatusBadge.jsx';
import { Dot } from '../../components/StatusWord.jsx';
import ApproveTenant, { ApprovalFacts } from '../../components/ApproveTenant.jsx';
import { needsTenantApproval } from '../../lib/consent-approval.js';
import { tierLabel } from '../../lib/tier-labels.js';
import { channelLabel } from '../../../../shared/channel.mjs';
import { decommissionLeftovers } from '../../../../shared/decommission-leftovers.mjs';
import { CONSENT_STATE_LABELS, consentState } from '../../lib/customer-status.js';
import { customerBucketName, timeAgo } from '../../lib/customer-format.js';

/**
 * The parts of Settings that came from the customer's Deployment section (portal workbench, 2026-09-29; split by the
 * ENTRAID-347 redesign, plan C6, into Settings > Microsoft connection, AWS deployment and Danger zone): the account
 * (Identity), the Microsoft consent status, the AWS account, stack, bucket, trust role, app secret and code version
 * (last code push), and the danger zone. Operator-only controls: approve the trust account, reset consent, the Trading
 * Partner ID with its source, update the collector, decommission and delete. Display only: every action's API route
 * checks the operator role itself.
 */

/**
 * A ref for the Cancel button of a confirm box: focus moves to it when the box
 * opens, so a keyboard or screen reader user lands inside the question (the
 * button that opened it is gone) and Enter cancels rather than confirms.
 */
function useCancelFocus(open) {
  const ref = useRef(null);
  useEffect(() => {
    if (open) ref.current?.focus();
  }, [open]);
  return ref;
}

// Badge per tone of the stored push outcome ('success' and 'current', which the fleet push writes when nothing had to
// change, are good; 'in-progress' is not a failure; anything else is).
const CODE_PUSH_BADGE = { good: 'badge-green', info: 'badge-amber', bad: 'badge-red' };

// Notes and confirmations carry a left rule in the status color with ink text: never a tinted fill, never amber text.
const ruled = (tone) => ({ borderLeft: `4px solid var(--${tone})` });

/** The danger zone (operators only, the caller decides): decommission, or delete the record once decommissioned. */
export function DangerZone({ customer, onRefresh }) {
  return customer.status === 'decommissioned' ? <DeleteRecordSection customer={customer} onChanged={onRefresh} /> : <DecommissionSection customer={customer} onDone={onRefresh} />;
}

export function IdentityCard({ customer, isOperator, onRefresh }) {
  // ENTRAID-294: names for the two account ids, editable by whoever can see
  // this page (operators and the customer admin; the API enforces it).
  const accounts = customerAccountFacts(customer);
  const hasAccounts = Boolean(accounts.deploymentAccountId || accounts.subscriberAccountId);
  return (
    <div className="card">
      <h2>Identity</h2>
      <dl className="mt-4 grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
        <dt className="text-slate-500">Customer stack prefix</dt>
        <dd>
          <span className="font-mono break-all">{customer.customerId}</span>
          <p className="mt-0.5 text-xs text-slate-500">onGuard's technical identifier, used in URLs and stack names. The business identifier is the Trading Partner ID.</p>
        </dd>
        <dt className="text-slate-500">Trading Partner ID</dt>
        <dd>
          <TradingPartnerIdField customer={customer} isOperator={isOperator} onSaved={onRefresh} />
        </dd>
        {hasAccounts && (
          <>
            <dt className="text-slate-500">AWS account names</dt>
            <dd>
              <AccountNamesField
                customer={customer}
                awsAccountId={accounts.deploymentAccountId}
                marketplaceAccountId={accounts.subscriberAccountId}
                onSaved={onRefresh}
              />
            </dd>
          </>
        )}
        <dt className="text-slate-500">Entra Tenant</dt>
        <dd className="font-mono break-all">{customer.entraTenantId ?? '--'}</dd>
        <dt className="text-slate-500">Tier</dt>
        <dd>{tierLabel(customer.tierName)}</dd>
        {customer.channel && (
          <>
            <dt className="text-slate-500">Channel</dt>
            <dd>{channelLabel(customer.channel)}</dd>
          </>
        )}
        {customer.productCode && (
          <>
            <dt className="text-slate-500">Marketplace product code</dt>
            <dd className="font-mono break-all">{customer.productCode}</dd>
          </>
        )}
        <dt className="text-slate-500">Status</dt>
        <dd><StatusBadge status={customer.status} /></dd>
        {customer.createdAt && (
          <>
            <dt className="text-slate-500">Created</dt>
            <dd>{new Date(customer.createdAt).toLocaleDateString()}</dd>
          </>
        )}
      </dl>
    </div>
  );
}

// Where the stored Trading Partner ID came from (customers table attribute
// tradingPartnerSource, ENTRAID-238). Operator-only: the customer view never
// receives it.
const TRADING_PARTNER_SOURCE_LABELS = {
  operator: 'set by an operator',
  activecampaign: 'from ActiveCampaign',
  'email-domain': 'from the email domain',
};

function tradingPartnerSourceLabel(source) {
  return TRADING_PARTNER_SOURCE_LABELS[source] ?? 'source not recorded';
}

/**
 * Inline edit for the Trading Partner ID (business identifier, distinct
 * from the technical customerId slug shown above it). Read-only for a
 * non-operator; an operator gets an Edit affordance that swaps in an
 * input with Save/Cancel. Save calls api.updateCustomer, which normalizes
 * and validates server-side (400 with a message on an invalid value).
 *
 * An operator also sees where the value came from. A value that is not yet
 * an operator's (from ActiveCampaign, derived from the email domain, or with
 * no recorded source) offers Confirm: it saves the same value again, which
 * the server records as the operator's, so it is never overwritten by the
 * ActiveCampaign feed.
 */
export function TradingPartnerIdField({ customer, isOperator, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(customer.tradingPartnerId || '');
  const [saved, setSaved] = useState(customer.tradingPartnerId || null);
  const [source, setSource] = useState(customer.tradingPartnerSource || null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setSaved(customer.tradingPartnerId || null);
    setValue(customer.tradingPartnerId || '');
  }, [customer.tradingPartnerId]);

  useEffect(() => {
    setSource(customer.tradingPartnerSource || null);
  }, [customer.tradingPartnerSource]);

  if (!isOperator) {
    return <span className="font-mono break-all">{saved || 'not set'}</span>;
  }

  async function handleConfirm() {
    setBusy(true); setError(null);
    try {
      const res = await api.updateCustomer(customer.customerId, { tradingPartnerId: saved });
      setSaved(res?.customer?.tradingPartnerId ?? saved);
      setSource(res?.customer?.tradingPartnerSource ?? 'operator');
      onSaved?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (!editing) {
    return (
      <span className="flex flex-col gap-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-mono break-all">{saved || 'not set'}</span>
          {saved && (
            <span className="text-xs text-slate-500">{tradingPartnerSourceLabel(source)}</span>
          )}
          <button
            type="button"
            className="text-xs text-ipc-light hover:underline"
            onClick={() => setEditing(true)}
            disabled={busy}
          >
            Edit
          </button>
          {saved && source !== 'operator' && (
            <button
              type="button"
              className="text-xs text-ipc-light hover:underline disabled:opacity-50"
              onClick={handleConfirm}
              disabled={busy}
            >
              {busy ? 'Confirming...' : 'Confirm'}
            </button>
          )}
        </span>
        {error && <span className="text-xs text-red-700">{error}</span>}
      </span>
    );
  }

  async function handleSave() {
    setBusy(true); setError(null);
    try {
      const normalized = value.trim().toUpperCase();
      const res = await api.updateCustomer(customer.customerId, { tradingPartnerId: normalized || null });
      const nextValue = res?.customer?.tradingPartnerId ?? (normalized || null);
      setSaved(nextValue);
      setSource(nextValue ? (res?.customer?.tradingPartnerSource ?? 'operator') : null);
      setEditing(false);
      onSaved?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  function handleCancel() {
    setValue(saved || '');
    setError(null);
    setEditing(false);
  }

  return (
    <span className="flex flex-col gap-1">
      <span className="flex items-center gap-2">
        <input
          className="input font-mono text-xs py-1 w-32"
          value={value}
          onChange={(e) => setValue(e.target.value.toUpperCase())}
          maxLength={32}
          disabled={busy}
        />
        <button
          type="button"
          className="text-xs px-2 py-1 rounded bg-ipc-light text-white hover:bg-ipc-primary transition-colors disabled:opacity-50"
          onClick={handleSave}
          disabled={busy}
        >
          {busy ? 'Saving...' : 'Save'}
        </button>
        <button
          type="button"
          className="text-xs px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-100 transition-colors"
          onClick={handleCancel}
          disabled={busy}
        >
          Cancel
        </button>
      </span>
      {error && <span className="text-xs text-red-700">{error}</span>}
    </span>
  );
}

export function DeploymentCard({ customer, isOperator, onRefresh }) {
  const [pushBusy, setPushBusy] = useState(false);
  const [pushResult, setPushResult] = useState(null);

  if (!customer.trustRoleArn && !customer.stackName) {
    return (
      <div className="card">
        <h2>Deployment</h2>
        <p className="text-sm text-slate-500 mt-4">No collector deployed yet. Overview shows the next step.</p>
      </div>
    );
  }

  const bucketName = customerBucketName(customer);
  const region = customer.awsRegion ?? 'us-east-1';
  // ENTRAID-266: the account shown is the one the registered trust role
  // lives in (where the stack and bucket are), never the Marketplace
  // subscriber account copied into awsAccountId.
  const accounts = customerAccountFacts(customer);
  // ENTRAID-294: names next to the ids (alias, else the stored name, else none).
  const awsName = awsAccountDisplayName(customer);
  const marketplaceName = marketplaceAccountDisplayName(customer, {
    sameAsDeployment: Boolean(accounts.subscriberAccountId) && accounts.subscriberAccountId === accounts.deploymentAccountId,
  });

  const credExpiry = customer.appCredentialExpiresAt ? new Date(customer.appCredentialExpiresAt) : null;
  const credExpired = credExpiry && credExpiry < new Date();
  const credExpiresSoon = credExpiry && !credExpired && (credExpiry - new Date()) < 30 * 86400000;

  async function handlePushCode() {
    setPushBusy(true); setPushResult(null);
    try {
      const res = await api.updateCode(customer.customerId);
      // Success only when the answer says so: the update call can answer 200 while the stack then rolls back.
      setPushResult(pushResultNotice(res, { trustOutdated: trustStackOutdated(customer) === true }));
      onRefresh?.();
    } catch (e) {
      setPushResult({ tone: 'bad', title: 'The collector was not updated', message: `${e.message} The collector keeps running the previous version. Try again, or contact IPC Global support.` });
    } finally {
      setPushBusy(false);
    }
  }

  // The on-demand run lives in the Run now panel (components/RunNow.jsx,
  // ENTRAID-277), next to the last run, for operators and customer admins.
  return (
    <div className="card">
      <div className="flex items-center justify-between">
        <h2>Deployment</h2>
        {isOperator && customer.status === 'deployed' && (
          <div className="flex gap-2">
            <button
              className="text-xs px-3 py-1.5 rounded-md bg-ipc-light text-white hover:bg-ipc-primary transition-colors disabled:opacity-50"
              onClick={handlePushCode}
              disabled={pushBusy}
            >
              {pushBusy ? 'Updating...' : 'Update collector'}
            </button>
          </div>
        )}
      </div>
      {pushResult && (
        <Notice tone={pushResult.tone} titleAs="h3" title={pushResult.title} testId="push-result" className="mt-2">
          {pushResult.message}
        </Notice>
      )}
      {isOperator && <AccountApprovalPanel customer={customer} accounts={accounts} onRefresh={onRefresh} />}
      {isOperator && accounts.mismatch && !accountApprovalNeeded(customer, accounts.deploymentAccountId) && (
        <div role="note" data-testid="account-mismatch" className="mt-3 rounded-lg border border-ipc-border bg-white px-3 py-2 text-xs text-ipc-ink" style={ruled('warn')}>
          The AWS Marketplace subscription account (<span className="font-mono">{accountLabel(marketplaceName, accounts.subscriberAccountId ?? accounts.recordedAccountId)}</span>)
          is not the account the trust role was registered in (<span className="font-mono">{accountLabel(awsName, accounts.deploymentAccountId)}</span>).
          The stack, the collector and the data bucket are in <span className="font-mono">{accountLabel(awsName, accounts.deploymentAccountId)}</span>.
          Confirm with the customer that this is the account they intended.
        </div>
      )}
      <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
        <div>
          <dt className="text-slate-500">AWS Account</dt>
          <dd className="font-mono mt-0.5 break-all" data-testid="deployment-account">{accountLabel(awsName, accounts.deploymentAccountId)}</dd>
        </div>
        {accounts.subscriberAccountId && (accounts.mismatch || marketplaceName) && (
          <div>
            <dt className="text-slate-500">Marketplace subscription account</dt>
            <dd className="font-mono mt-0.5 break-all" data-testid="marketplace-account">{accountLabel(marketplaceName, accounts.subscriberAccountId)}</dd>
          </div>
        )}
        <div>
          <dt className="text-slate-500">Region</dt>
          <dd className="font-mono mt-0.5">{region}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Entra App Secret</dt>
          <dd className="mt-0.5">
            {customer.appCredentialKeyId ? (
              <span className="flex items-center gap-2">
                <span className="font-mono text-xs">{customer.appCredentialKeyId.substring(0, 8)}...</span>
                {credExpired && <span className="badge-red">expired</span>}
                {credExpiresSoon && <span className="badge-amber">expires soon</span>}
                {credExpiry && (
                  <span className="text-xs text-slate-500" data-testid="secret-expiry">
                    {credExpired ? 'expired' : 'expires'} {credExpiry.toLocaleDateString()}
                  </span>
                )}
              </span>
            ) : '--'}
          </dd>
        </div>
        {isOperator && customer.appCredentialKeyId && (
          <div>
            <dt className="text-slate-500">Last secret rotation</dt>
            <dd className="mt-0.5 text-xs" data-testid="secret-rotation">
              {customer.lastRotatedAt ? timeAgo(customer.lastRotatedAt) : 'Not rotated yet (automatic from 30 days before expiry)'}
              {rotationFailedSince(customer) && (
                <span className="block mt-1 text-red-700" role="alert">
                  Last attempt failed {timeAgo(customer.lastRotationFailedAt)}: {customer.lastRotationError ?? 'no detail recorded'}
                </span>
              )}
            </dd>
          </div>
        )}
      </dl>

      <section className="mt-6" aria-labelledby="deployment-trust-heading" data-testid="deployment-trust">
        <h3 id="deployment-trust-heading" className="text-sm font-semibold text-ipc-ink">Trust connection</h3>
        <p className="text-xs text-slate-500 mt-0.5">The access role onGuard uses in your AWS account. You update it, when onGuard asks.</p>
        <TrustStackNotice customer={customer} onRefresh={onRefresh} />
        <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <div>
            <dt className="text-slate-500">Cross-account Role</dt>
            <dd className="font-mono text-xs mt-0.5 break-all">{customer.trustRoleArn ?? '--'}</dd>
          </div>
          {customer.trustRoleArn && (
            <div>
              <dt className="text-slate-500">Access role version</dt>
              <dd className="mt-0.5 text-xs" data-testid="trust-stack-version">
                {Number.isInteger(customer.trustStackVersion) ? `${customer.trustStackVersion} of ${TRUST_STACK_VERSION}` : 'Not checked yet'}
                {trustStackOutdated(customer) === true && <span className="ml-2 badge-amber">update needed</span>}
              </dd>
            </div>
          )}
        </dl>
      </section>

      <section className="mt-6" aria-labelledby="deployment-resources-heading" data-testid="deployment-resources">
        <h3 id="deployment-resources-heading" className="text-sm font-semibold text-ipc-ink">Resources</h3>
        <p className="text-xs text-slate-500 mt-0.5">The collector, the data bucket and the reports. onGuard updates them.</p>
        <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <div>
            <dt className="text-slate-500">Stack</dt>
            <dd className="font-mono text-xs mt-0.5 break-all">{customer.stackName ?? '--'}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Data Bucket</dt>
            <dd className="font-mono text-xs mt-0.5 break-all">{bucketName ?? '--'}</dd>
          </div>
          {customer.lastCodePushAt && (
            <div>
              <dt className="text-slate-500">Last code push</dt>
              <dd className="mt-0.5 text-xs" data-testid="last-code-push">
                {timeAgo(customer.lastCodePushAt)}
                <span className={`ml-2 ${CODE_PUSH_BADGE[resourcesStatus(customer)?.tone] ?? 'badge-red'}`}>
                  {resourcesStatus(customer)?.label ?? customer.lastCodePushStatus}
                </span>
                {customer.lastCodePushStatus === 'failed' && (
                  <span className="block mt-1 text-slate-600">
                    The update was rolled back or did not finish. The collector keeps running the previous version.
                  </span>
                )}
              </dd>
            </div>
          )}
        </dl>
      </section>
    </div>
  );
}

function DecommissionSection({ customer, onDone }) {
  const [confirming, setConfirming] = useState(false);
  const [deleteStack, setDeleteStack] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const cancelRef = useCancelFocus(confirming);

  async function handleDecommission() {
    setBusy(true); setError(null);
    try {
      await api.decommission(customer.customerId, { deleteStack });
      onDone?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  return (
    <div className="card" style={{ borderLeft: '4px solid var(--bad)' }}>
      <h2>Danger zone</h2>
      <p className="text-sm text-slate-600 mt-2">
        Decommissioning revokes the Entra app secret so data collection stops immediately.
        The customer's S3 data bucket is preserved.
      </p>
      {!confirming ? (
        <button className="btn-danger mt-4" onClick={() => setConfirming(true)}>
          Decommission this customer
        </button>
      ) : (
        <div className="mt-4 space-y-3">
          <div className="rounded-xl border border-ipc-border bg-white p-4">
            <p className="text-sm font-medium text-red-700">Confirm decommission of {customer.companyName || customer.customerId}</p>
            <label className="flex items-center gap-2 mt-3 text-sm">
              <input type="checkbox" checked={deleteStack} onChange={(e) => setDeleteStack(e.target.checked)} />
              Also delete the CloudFormation stack (Lambda, EventBridge rule, config table)
            </label>
            <p className="text-xs text-slate-500 mt-1 ml-6">
              The S3 data bucket is always preserved regardless of this setting.
            </p>
          </div>
          {error && <div className="text-sm text-red-700">{error}</div>}
          <div className="flex gap-3">
            <button className="btn-danger" onClick={handleDecommission} disabled={busy}>
              {busy ? 'Decommissioning...' : 'Confirm decommission'}
            </button>
            <button ref={cancelRef} className="btn-secondary" onClick={() => setConfirming(false)} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ENTRAID-307: what the decommission left behind, and the one cleanup the portal offers for it: releasing the Entra
// tenant so it can be connected to another customer (the record stays). Nothing else here deletes IdP or Entra objects.
function LeftBehind({ customer, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const items = decommissionLeftovers(customer);
  if (items.length === 0) return null;

  async function release() {
    setBusy(true); setError(null);
    try {
      await api.releaseTenant(customer.customerId);
      onChanged?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3">
      <h4 className="text-xs font-medium text-slate-700">Still in place</h4>
      <ul className="mt-1 space-y-1 text-xs text-slate-600">
        {items.map((x) => (
          <li key={x.id}>
            <span className="font-medium text-slate-700">{x.label}.</span> {x.detail}
            {x.id === 'tenant-binding' ? (
              <button type="button" className="ml-2 text-xs underline text-slate-700 disabled:opacity-50" onClick={release} disabled={busy}>
                {busy ? 'Releasing...' : 'Release tenant'}
              </button>
            ) : null}
          </li>
        ))}
      </ul>
      {error ? <div role="alert" className="mt-1 text-xs text-red-700">{error}</div> : null}
    </div>
  );
}

/**
 * ENTRAID-350: what the operator must know when the delete could not remove every sign-in access of the customer on
 * the IPC Global identity platform (DELETE answers `idpCleanup`). Null when it all went, or when the answer has none.
 */
export function idpCleanupNotice(idpCleanup) {
  if (!idpCleanup || idpCleanup.result === 'done') return null;
  const left = Number.isInteger(idpCleanup.leftovers) && idpCleanup.leftovers > 0 ? ` ${idpCleanup.leftovers} sign-in access${idpCleanup.leftovers === 1 ? '' : 'es'} may remain.` : '';
  return `The record was deleted, but its people could not all be removed from the IPC Global sign-in.${left} Run the stale memberships cleanup (runbook: stale IdP memberships) with this customer id.`;
}

function DeleteRecordSection({ customer, onChanged }) {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [deletedNotice, setDeletedNotice] = useState(null);
  const cancelRef = useCancelFocus(confirming);

  async function handleDelete() {
    setBusy(true); setError(null);
    try {
      const r = await api.deleteCustomer(customer.customerId);
      const notice = idpCleanupNotice(r?.idpCleanup);
      if (notice) setDeletedNotice(notice);
      else navigate('/customers', { replace: true });
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border border-slate-200 rounded-lg p-6 bg-slate-50/50">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-sm bg-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5">
          <svg className="w-4 h-4 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
          </svg>
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-medium text-slate-700">Customer decommissioned</h3>
          <p className="text-xs text-slate-500 mt-1">
            This customer was decommissioned
            {customer.decommissionedAt && <> on {new Date(customer.decommissionedAt).toLocaleDateString()}</>}.
            You can verify the cleanup in the AWS Console before permanently removing this record.
            Deleting the record also releases its Entra tenant and removes its people from the IPC Global sign-in.
          </p>
          <LeftBehind customer={customer} onChanged={onChanged} />

          {deletedNotice ? (
            <div className="mt-3 space-y-2">
              <div role="alert" className="flex items-start gap-2 text-xs text-slate-700 border border-slate-200 rounded-md px-3 py-2">
                <Dot tone="warn" className="mt-1" />
                <span>{deletedNotice}</span>
              </div>
              <button
                className="text-xs px-3 py-1.5 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-100 transition-colors"
                onClick={() => navigate('/customers', { replace: true })}
              >
                Back to customers
              </button>
            </div>
          ) : !confirming ? (
            <button
              className="mt-3 text-xs px-3 py-1.5 rounded-md border border-red-300 text-red-700 hover:bg-red-50 transition-colors"
              onClick={() => setConfirming(true)}
            >
              Delete this record
            </button>
          ) : (
            <div className="mt-3 space-y-2">
              <p className="text-xs text-red-700 font-medium">
                Permanently delete <strong>{customer.companyName || customer.customerId}</strong> from the portal? This cannot be undone.
              </p>
              {error && <div className="text-xs text-red-700">{error}</div>}
              <div className="flex gap-2">
                <button
                  className="text-xs px-3 py-1.5 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
                  onClick={handleDelete}
                  disabled={busy}
                >
                  {busy ? 'Deleting...' : 'Confirm delete'}
                </button>
                <button
                  ref={cancelRef}
                  className="text-xs px-3 py-1.5 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-100 transition-colors"
                  onClick={() => setConfirming(false)}
                  disabled={busy}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * SEC-004 / ENTRAID-272: does the registered trust role's account still need
 * an operator's approval before onGuard deploys there? Mirrors the backend
 * rule (lib/trust-account.mjs): the Marketplace account and an approved
 * account are fine, and an existing deployment is never blocked.
 */
export function accountApprovalNeeded(customer, deploymentAccountId) {
  if (!deploymentAccountId) return false;
  if (customer?.trustAccountApproved === deploymentAccountId && customer?.trustAccountApprovedAt) return false;
  if (customer?.stackName) return false;
  const marketplace = /^\d{12}$/.test(String(customer?.awsAccountId ?? '')) ? customer.awsAccountId : null;
  return marketplace !== deploymentAccountId;
}

function AccountApprovalPanel({ customer, accounts, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const account = accounts.deploymentAccountId;
  if (!accountApprovalNeeded(customer, account)) return null;
  const request = customer.trustAccountApprovalRequest?.accountId === account ? customer.trustAccountApprovalRequest : null;

  async function approve() {
    setBusy(true); setError(null);
    try {
      await api.updateCustomer(customer.customerId, { approveTrustAccount: account });
      onRefresh?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div role="note" data-testid="account-approval" className="mt-3 space-y-2 rounded-lg border border-ipc-border bg-white px-3 py-2 text-xs text-ipc-ink" style={ruled('warn')}>
      <p>
        The customer's AWS stack is in account <span className="font-mono">{account}</span>, which is not its AWS
        Marketplace account{(accounts.subscriberAccountId ?? accounts.recordedAccountId) ? <> (<span className="font-mono">{accounts.subscriberAccountId ?? accounts.recordedAccountId}</span>)</> : ' (none on record)'}.
        onGuard deploys there only after an operator approves it.
        {request && <> Approval requested {new Date(request.requestedAt).toLocaleString()}{request.requestedBy ? ` by ${request.requestedBy}` : ''}.</>}
      </p>
      <p>Confirm with the customer that the account is theirs before approving.</p>
      <button
        type="button"
        className="px-3 py-1.5 rounded-md bg-ipc-light text-white hover:bg-ipc-primary transition-colors disabled:opacity-50"
        onClick={approve}
        disabled={busy}
      >
        {busy ? 'Approving...' : `Approve account ${account}`}
      </button>
      {error && <p className="text-red-700" role="alert">{error}</p>}
    </div>
  );
}

// ENTRAID-271: a failure newer than the last successful rotation.
export function rotationFailedSince(customer) {
  const failed = Date.parse(customer?.lastRotationFailedAt ?? '');
  if (Number.isNaN(failed)) return false;
  const rotated = Date.parse(customer?.lastRotatedAt ?? '');
  return Number.isNaN(rotated) || failed > rotated;
}

/** Microsoft admin consent, with the operator-only reset (ENTRAID-261). */
export function ConsentCard({ customer, isOperator, onRefresh }) {
  const state = consentState(customer);
  const [approvedNote, setApprovedNote] = useState(false);
  return (
    <div className="card" data-testid="consent-card">
      <h2>Microsoft consent</h2>
      <dl className="mt-4 grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
        <dt className="text-slate-500">Status</dt>
        <dd data-testid="consent-status">{CONSENT_STATE_LABELS[state]}</dd>
        {customer.entraTenantVerifiedAt && (
          <>
            <dt className="text-slate-500">Tenant verified</dt>
            <dd>{new Date(customer.entraTenantVerifiedAt).toLocaleDateString()}</dd>
          </>
        )}
        {customer.entraConsentResetAt && (
          <>
            <dt className="text-slate-500">Consent reset</dt>
            <dd>{new Date(customer.entraConsentResetAt).toLocaleDateString()}</dd>
          </>
        )}
      </dl>
      {isOperator === true && needsTenantApproval(customer) && (
        <div role="note" data-testid="tenant-approval" className="mt-4 space-y-3 rounded-lg border border-ipc-border bg-white p-3 text-sm text-ipc-ink" style={ruled('warn')}>
          <p>A Microsoft admin signed in, but onGuard could not connect the tenant automatically. Check the facts with the customer, then approve.</p>
          <ApprovalFacts customer={customer} />
          <ApproveTenant customer={customer} isOperator={isOperator} onApproved={() => { setApprovedNote(true); onRefresh?.(); }} onStale={() => onRefresh?.()} />
        </div>
      )}
      {approvedNote && <p role="status" className="mt-4 text-sm text-slate-600">Tenant approved. Consent is confirmed.</p>}
      <MissingPermissionsNote customer={customer} onRefresh={onRefresh} />
      {isOperator && customer.entraTenantId && !customer.entraConsentResetAt && (
        <ResetConsent customer={customer} onDone={onRefresh} />
      )}
    </div>
  );
}

function ResetConsent({ customer, onDone }) {
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const cancelRef = useCancelFocus(confirming);

  async function reset() {
    setBusy(true);
    setError(null);
    try {
      await api.updateCustomer(customer.customerId, { resetEntraConsent: true });
      setDone(true);
      setConfirming(false);
      onDone?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  // The reset went through: no second button while the record reloads.
  if (done) return <p role="status" className="mt-4 text-sm text-slate-600">Consent reset. Refreshing...</p>;
  if (!confirming) {
    return (
      <button type="button" className="btn-secondary mt-4 text-sm" onClick={() => setConfirming(true)}>
        Reset consent
      </button>
    );
  }
  return (
    <div role="group" aria-label="Confirm consent reset" className="mt-4 space-y-3 rounded-lg border border-ipc-border bg-white p-3 text-sm text-ipc-ink" style={ruled('warn')}>
      <p>
        This sends {customer.companyName || customer.customerId} back to the Microsoft admin-consent sign-in. The
        collector keeps its configuration and fails until consent is granted again.
      </p>
      {error && <p role="alert" className="text-red-700">{error}</p>}
      <div className="flex gap-2">
        <button type="button" className="btn-danger text-sm" onClick={reset} disabled={busy}>
          {busy ? 'Resetting...' : 'Confirm reset'}
        </button>
        <button ref={cancelRef} type="button" className="btn-secondary text-sm" onClick={() => setConfirming(false)} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}
